import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Star, Users, Briefcase, Upload, LogOut, BarChart3, UserPlus, Shield, Menu, X, Settings } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: false },
  { path: "/swipe", label: "Review", icon: Star, adminOnly: false },
  { path: "/scores", label: "Scores", icon: BarChart3, adminOnly: false },
  { path: "/candidates", label: "Candidates", icon: Users, adminOnly: false },
  { path: "/roles", label: "Roles", icon: Briefcase, adminOnly: false },
  { path: "/team", label: "Team", icon: UserPlus, adminOnly: false },
  { path: "/upload", label: "Upload / Sync", icon: Upload, adminOnly: true },
  { path: "/settings", label: "Settings", icon: Settings, adminOnly: true },
];

export const Sidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const sidebarContent = (
    <>
      {/* Brand */}
      <div className="p-5 lg:p-6 border-b border-white/10 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg lg:text-xl font-bold tracking-tight text-white uppercase">
            Emergent <span className="text-blue-500">ATS</span>
          </h1>
          <p className="text-[10px] text-zinc-500 tracking-wider uppercase">Talent Matcher</p>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800"
          data-testid="close-sidebar-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 lg:px-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          const isActive = location.pathname === item.path ||
            (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/[\s/]/g, "-")}`}
              onClick={() => handleNav(item.path)}
              className={`sidebar-item w-full flex items-center gap-3 px-3 lg:px-4 py-2.5 rounded-lg text-sm font-medium ${
                isActive
                  ? "active text-blue-400 bg-blue-500/10 border-l-[3px] border-blue-500"
                  : "text-zinc-400 hover:text-zinc-200 border-l-[3px] border-transparent"
              }`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
              {item.adminOnly && <Shield className="w-3 h-3 text-amber-500 ml-auto" />}
            </button>
          );
        })}
      </nav>

      {/* User */}
      {user && (
        <div className="p-3 lg:p-4 border-t border-white/10">
          <div className="flex items-center gap-2.5 mb-2.5">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-zinc-700 text-xs">{user.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-zinc-200 truncate">{user.name}</p>
                {user.role === "admin" && (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0">Admin</Badge>
                )}
              </div>
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-40 lg:hidden p-2 rounded-lg bg-zinc-900/90 border border-white/10 text-zinc-300 hover:text-white backdrop-blur-sm"
        data-testid="open-sidebar-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-60 xl:w-64 h-screen bg-[#0f0f12] border-r border-white/10 flex-col shrink-0"
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0f0f12] border-r border-white/10 flex flex-col lg:hidden transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
};
